import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { EmailPageLayout } from "@/components/email/EmailPageLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { MetricsPanel } from "@/components/email/charts";
import { BarChart3, ShieldCheck, Mail, RefreshCcw } from "lucide-react";

export default function EmailPerformance() {
  // State for real SES notifications and email logs
  const [bounces, setBounces] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("performance");

  // Function to fetch SES notification data and email logs
  const fetchSESData = async () => {
    setLoading(true);

    try {
      // Fetch bounces
      const { data: bounceData, error: bounceError } = await supabase
        .from("email_bounces")
        .select("*")
        .order("timestamp", { ascending: false });

      if (bounceError) {
        console.error("Error fetching bounce data:", bounceError);
      }

      // Fetch complaints
      const { data: complaintData, error: complaintError } = await supabase
        .from("email_complaints")
        .select("*")
        .order("timestamp", { ascending: false });

      if (complaintError) {
        console.error("Error fetching complaint data:", complaintError);
      }

      // Fetch deliveries
      const { data: deliveryData, error: deliveryError } = await supabase
        .from("email_deliveries")
        .select("*")
        .order("timestamp", { ascending: false });

      if (deliveryError) {
        console.error("Error fetching delivery data:", deliveryError);
      }

      // Fetch email logs
      const { data: emailLogData, error: emailLogError } = await supabase
        .from("email_logs")
        .select("*")
        .order("timestamp", { ascending: false });

      if (emailLogError) {
        console.error("Error fetching email log data:", emailLogError);
      }

      setBounces(bounceData || []);
      setComplaints(complaintData || []);
      setDeliveries(deliveryData || []);
      setEmailLogs(emailLogData || []);
    } catch (error) {
      console.error("Error fetching SES data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchSESData();
  }, []);

  // Calculate summary metrics from real data
  const totalSent = emailLogs.length;
  const totalDelivered = deliveries.length;
  const totalBounced = bounces.length;
  const totalComplaints = complaints.length;

  // Calculate bounce and complaint rates based on real data
  const bounceRate =
    totalSent > 0 ? ((totalBounced / totalSent) * 100).toFixed(1) : "0.0";
  const complaintRate =
    totalSent > 0 ? ((totalComplaints / totalSent) * 100).toFixed(2) : "0.00";

  // Calculate delivery rate for the dashboard
  const deliveryRate =
    totalSent > 0 ? ((totalDelivered / totalSent) * 100).toFixed(1) : "0.0";

  return (
    <EmailPageLayout
      title="Email Performance Metrics"
      description="Monitor your email delivery performance and compliance metrics"
      action={
        <Button
          variant="outline"
          size="sm"
          className="h-8 px-3 text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/40"
          onClick={fetchSESData}
          disabled={loading}
        >
          <RefreshCcw
            className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`}
          />
          {loading ? "Refreshing..." : "Refresh Data"}
        </Button>
      }
    >
      <div className="space-y-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-8">
            <TabsTrigger
              value="performance"
              className="flex items-center gap-2"
            >
              <BarChart3 className="h-4 w-4" />
              Performance Metrics
            </TabsTrigger>
            <TabsTrigger value="compliance" className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" />
              Compliance
            </TabsTrigger>
            <TabsTrigger
              value="authentication"
              className="flex items-center gap-2"
            >
              <Mail className="h-4 w-4" />
              Authentication
            </TabsTrigger>
          </TabsList>

          <TabsContent value="performance" className="space-y-6">
            <MetricsPanel
              bounceRate={bounceRate}
              complaintRate={complaintRate}
              deliveryRate={deliveryRate}
              timeRange="Last 30 days"
            />

            <div className="bg-gray-950 border border-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-white mb-4">
                Performance Summary
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-gray-900 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-400 mb-2">
                    Total Sent
                  </h3>
                  <p className="text-2xl font-bold text-white">{totalSent}</p>
                </div>
                <div className="bg-gray-900 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-400 mb-2">
                    Delivered
                  </h3>
                  <p className="text-2xl font-bold text-white">
                    {totalDelivered}
                  </p>
                </div>
                <div className="bg-gray-900 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-400 mb-2">
                    Bounced
                  </h3>
                  <p className="text-2xl font-bold text-white">
                    {totalBounced}
                  </p>
                </div>
                <div className="bg-gray-900 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-400 mb-2">
                    Complaints
                  </h3>
                  <p className="text-2xl font-bold text-white">
                    {totalComplaints}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-950 border border-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-white mb-4">
                AWS SES Limits
              </h2>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-gray-400">Sending Quota</span>
                    <span className="text-sm text-gray-200">50,000 / day</span>
                  </div>
                  <div className="w-full bg-gray-800 rounded-full h-2">
                    <div
                      className="h-2 rounded-full bg-blue-500"
                      style={{ width: `${(totalSent / 50000) * 100}%` }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-gray-400">Sending Rate</span>
                    <span className="text-sm text-gray-200">14 / second</span>
                  </div>
                  <div className="w-full bg-gray-800 rounded-full h-2">
                    <div
                      className="h-2 rounded-full bg-green-500"
                      style={{ width: "35%" }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="compliance" className="space-y-6">
            <div className="bg-gray-950 border border-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-white mb-4">
                Compliance Status
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-gray-900 rounded-lg">
                  <div className="mt-1">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div>
                    <h3 className="text-md font-medium text-white">
                      Bounce Rate Compliance
                    </h3>
                    <p className="text-sm text-gray-400 mt-1">
                      Your current bounce rate of {bounceRate}% is below the AWS
                      recommended limit of 2%.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-gray-900 rounded-lg">
                  <div className="mt-1">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div>
                    <h3 className="text-md font-medium text-white">
                      Complaint Rate Compliance
                    </h3>
                    <p className="text-sm text-gray-400 mt-1">
                      Your current complaint rate of {complaintRate}% is below
                      the AWS recommended limit of 0.1%.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="authentication" className="space-y-6">
            <div className="bg-gray-950 border border-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-white mb-4">
                Email Authentication
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-gray-900 rounded-lg">
                  <div className="mt-1">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div>
                    <h3 className="text-md font-medium text-white">
                      SPF Record
                    </h3>
                    <p className="text-sm text-gray-400 mt-1">
                      SPF record is properly configured for your domain.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-gray-900 rounded-lg">
                  <div className="mt-1">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div>
                    <h3 className="text-md font-medium text-white">
                      DKIM Signatures
                    </h3>
                    <p className="text-sm text-gray-400 mt-1">
                      DKIM is properly configured and active for your domain.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-gray-900 rounded-lg">
                  <div className="mt-1">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div>
                    <h3 className="text-md font-medium text-white">
                      DMARC Policy
                    </h3>
                    <p className="text-sm text-gray-400 mt-1">
                      DMARC policy is properly configured for your domain.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </EmailPageLayout>
  );
}
